import { ReactNode } from 'react';
import { Pressable, PressableProps, StyleProp, ViewStyle, ActivityIndicator } from 'react-native';
import { useTheme } from '@/theme';
import { ThemedText } from './Text';
import { hapticMedium } from '@/utils/haptics';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'outlined' | 'filled' | 'danger' | 'text';
type ButtonSize = 'small' | 'medium' | 'large';

export type ButtonProps = PressableProps & {
  label: string;
  variant?: ButtonVariant;
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
  style?: StyleProp<ViewStyle>;
  size?: ButtonSize;
  loading?: boolean;
  labelNumberOfLines?: number;
  /** If false, skips impact haptic on press in (e.g. overlays with many button taps). */
  hapticOnPressIn?: boolean;
};

export function Button({ 
  label, 
  variant = 'primary', 
  icon, 
  iconPosition = 'left', 
  style, 
  size = 'medium',
  loading = false, 
  labelNumberOfLines = 1,
  hapticOnPressIn = true,
  ...rest 
}: ButtonProps) {
  const theme = useTheme();

  const isDark = theme.colorScheme === 'dark';
  const baseStyle: Record<ButtonVariant, ViewStyle> = {
    primary: {
      backgroundColor: theme.colors.primary,
      borderColor: theme.colors.primary,
    },
    filled: {
      backgroundColor: theme.colors.primary,
      borderColor: theme.colors.primary,
    },
    secondary: {
      backgroundColor: theme.colors.surface,
      borderColor: isDark ? theme.colors.border : theme.colors.primary,
    },
    outlined: {
      backgroundColor: theme.colors.surface,
      borderColor: isDark ? theme.colors.border : theme.colors.primary,
    },
    ghost: {
      backgroundColor: 'transparent',
      borderColor: 'transparent',
    },
    danger: {
      backgroundColor: theme.colors.error,
      borderColor: theme.colors.error,
    },
    text: {
      backgroundColor: 'transparent',
      borderColor: 'transparent',
    },
  };

  const textColor: Record<ButtonVariant, string> = {
    primary: theme.colors.onPrimary,
    filled: theme.colors.onPrimary,
    secondary: theme.colors.primary,
    outlined: theme.colors.primary,
    ghost: theme.colors.text,
    danger: theme.colors.onError,
    text: theme.colors.primary,
  };

  // Size map for consistent touch targets
  const sizePaddingVertical: Record<ButtonSize, number> = {
    small: theme.spacing(1),
    medium: theme.spacing(1.75),
    large: theme.spacing(2.25),
  };

  const sizePaddingHorizontal: Record<ButtonSize, number> = {
    small: theme.spacing(1.5),
    medium: theme.spacing(2),
    large: theme.spacing(2.5),
  };

  const sizeMinHeight: Record<ButtonSize, number> = {
    small: 44,
    medium: 44,
    large: 52,
  };

  const paddingVertical = sizePaddingVertical[size];
  const paddingHorizontal = sizePaddingHorizontal[size];
  const minHeight = Math.max(sizeMinHeight[size], paddingVertical * 2);

  const { onPress, onPressIn, ...pressableProps } = rest;

  // Guard against invalid accessibilityState values (Android can crash if these are not valid types).
  const rawAccessibilityState = {
    disabled: !!(rest.disabled || loading),
    ...(rest.accessibilityState || {}),
  } as Record<string, unknown>;
  const accessibilityState = (['disabled', 'selected', 'busy', 'expanded', 'checked'] as const).reduce(
    (acc, key) => {
      const v = rawAccessibilityState[key];
      if (typeof v === 'boolean') acc[key] = v;
      // RN types allow "mixed" for checked on some platforms; keep it if provided.
      if (key === 'checked' && v === 'mixed') acc[key] = v;
      return acc;
    },
    {} as Record<string, boolean | 'mixed'>
  );

  return (
    <Pressable
      {...pressableProps}
      disabled={!!(rest.disabled || loading)}
      accessibilityRole="button"
      accessibilityLabel={rest.accessibilityLabel || label}
      accessibilityHint={rest.accessibilityHint}
      accessibilityState={accessibilityState}
      onPress={(e) => {
        if (!rest.disabled && !loading && onPress) {
          onPress(e);
        }
      }}
      onPressIn={(e) => {
        if (hapticOnPressIn && !rest.disabled && !loading) {
          hapticMedium();
        }
        onPressIn?.(e);
      }}
      style={({ pressed }) => [
        {
          minHeight,
          paddingVertical: paddingVertical,
          paddingHorizontal,
          borderRadius: theme.radii.xl,
          borderWidth: 1,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: theme.spacing(1),
          opacity: (rest.disabled || loading ? 0.6 : pressed ? 0.85 : 1),
          transform: [{ scale: pressed && !rest.disabled && !loading ? 0.98 : 1 }],
        },
        baseStyle[variant],
        style,
      ]}
    >
      {loading ? (
        <ActivityIndicator 
          size="small" 
          color={textColor[variant]} 
        />
      ) : (
        <>
          {iconPosition === 'left' && icon}
          <ThemedText
            variant="bodyStrong"
            color={textColor[variant]}
            numberOfLines={labelNumberOfLines}
            ellipsizeMode="tail"
          >
            {label}
          </ThemedText>
          {iconPosition === 'right' && icon}
        </>
      )}
    </Pressable>
  );
}

